import {EventEmitter, Injectable, inject} from '@angular/core';
import {BehaviorSubject, distinctUntilChanged, Observable, of, Subject, Subscription, switchMap, throttleTime, timer, zip} from 'rxjs';
import {filter, first, map, shareReplay, tap} from 'rxjs/operators';
import {NavigationEnd, Router} from '@angular/router';
import {UsersService} from './data/users.service';
import {SecurityService} from './data/security.service';
import {containsIgnoreCase, isNotBlank} from '../_utils/lang';
import {DecodedJwtToken, JwtToken, RawDecodedTokenPair, TokenPair} from '../_data/admin/jwt-token';
import {JwtHelperService} from '@auth0/angular-jwt';
import {ConfirmModalService} from './gui/confirm-modal.service';
import {Branch} from '../_data/admin/branch';
import {expiryMillisFromNow, parseUser} from '../_utils/tokens';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {
  BUSINESS_REQUEST_CACHE_KEY,
  BUSINESS_RESPONSE_CACHE_KEY,
  CONSUMER_REQUEST_CACHE_KEY,
  CONSUMER_RESPONSE_CACHE_KEY,
  IFS_REQUEST_CACHE_KEY,
  IFS_RESPONSE_CACHE_KEY
} from '../_constants/constants';
import {anyTrue, exists} from '../_utils/operators';
import {UserDetails} from '../_data/mfa/mfa-user-details';
import {UserPermissions} from '../_data/admin/permissions';
import {User} from '../_data/admin/user';
import {CompaniesService} from './data/companies.service';
import {toUserPermissions} from '../_utils/users';
import {isError} from '../_utils/http';
import {isTokenPair} from '../_utils/type-guards';
import * as _ from 'lodash';

const jwtHelperService = new JwtHelperService();

/**
 * A service to help with authenticating a user's login/session.
 *
 * Most of the data is presented by Observables that different parts of the app subscribe to for what they want to know.
 *
 * The starting points for almost all of the streams are the tokens which get stored in behaviour subjects.
 * There are helper streams that subscribe to those subjects and convert them into things like the username, admins, branches, etc.
 *
 * The token data typically flows like so:
 * <ol>
 *   <li>API (raw auth token)</li>
 *   <li>Raw token goes into token$ subject</li>
 *   <li>Observable takes raw data from token$ subject, decodes it, and maps it as necessary</li>
 *   <li>Other parts of the site subscribe to these different observables to get the data they need</li>
 * </ol>
 *
 * There is also the {@link GuiPermissionsService} which is closely related to this service.
 * The difference can be explained primarily as: This auth service answers what the user IS, the gui permissions service answers what the user can SEE/DO.
 *
 */
@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  /**
   * Holds the raw token values.  Used primarily by the JwtInterceptor to set the header.
   * Private subscriptions within this class take data from here, decode it, and emit to the two decoded___Token Subjects for when the data within the tokens is needed.
   */
  readonly token$: BehaviorSubject<TokenPair<any, any> | null> = new BehaviorSubject<TokenPair<any, any> | null>(JSON.parse(sessionStorage.getItem('token')));

  /** Holds the decoded Auth token data.  It's primarily used during the MFA process (when the Refresh token hasn't been retrieved yet). */
  readonly decodedAuthToken$: Observable<DecodedJwtToken | null>;

  /** Holds the decoded Refresh token data.  It's the most used data from this service throughout the site. */
  readonly decodedAccessToken$: Observable<DecodedJwtToken | null>;

  /**
   * Returns either the Auth token or the Access token whenever one of those updates.
   * Normally the refresh is the one you want.  Unless you need to do something during the login process too, before the refresh token is fetched.
   */
  readonly decodedToken$: Observable<DecodedJwtToken | null>;

  /** Has a user STARTED to login? (Successfully done username/password but might not have finished MFA or first-time-user setup) */
  readonly isUserPartiallyLoggedIn$: Observable<boolean>;

  /** Is a user logged-in? */
  readonly isUserLoggedIn$: Observable<boolean>;

  /** Is the logged-in user a first-time user? */
  readonly isFirstTimeUser$: Observable<boolean>;

  /** Is the logged-in user's password expired? */
  readonly isPasswordExpired$: Observable<boolean>;

  /** Is logged-in user a TU Admin? */
  readonly isTuAdmin$: Observable<boolean>;

  /** Is logged-in user a company admin? */
  readonly isAdmin$: Observable<boolean>;

  /** Is logged-in user a TU Admin or Company Admin? Alias for boolean-or of isTuAdmin$ and isAdmin$ */
  readonly isTuAdminOrAdmin$: Observable<boolean>;

  /** Is logged-in user an operator? */
  readonly isOperator$: Observable<boolean>;

  /** Is logged-in user a business user? */
  readonly isBusiness$: Observable<boolean>;

  /** Is logged-in user a consumer user? */
  readonly isConsumer$: Observable<boolean>;

  /** Holds the home page of the logged-in user */
  readonly homePage$: Observable<string>;

  /** Holds the admin names for the logged-in user */
  readonly myAdminNames$: Observable<string[]>;

  /** Get the username of the currently logged-in user */
  readonly userName$: Observable<string>;

  /** Get the company name of the currently logged-in user */
  readonly companyName$: Observable<string>;

  /** The branches the currently logged-in user is assigned to */
  readonly userBranches$: Observable<Branch[]>;

  /** The UserPermissions object for the currently logged-in user */
  readonly userPermissions$: Observable<UserPermissions>;

  /** An Observable to monitor when the app is doing a logout (useful for components that will need to change state) */
  readonly logout$: EventEmitter<void> = new EventEmitter<void>();

  /** Anything that wants to refresh the session will emit a boolean through this subject.  The value emitted represents if the session should forcefully be refreshed. */
  private _manuallyInitSessionRefresh$: Subject<boolean> = new Subject<boolean>();

  private readonly router = inject(Router);
  private readonly securityService = inject(SecurityService);
  private readonly usersService = inject(UsersService);
  private readonly companiesService = inject(CompaniesService);
  private readonly modalService = inject(ConfirmModalService);

  private _refreshSession: Subscription;
  private _expiredSession: Subscription;
  private _modalSubscription: Subscription;

  private refreshSessionModalRef: NgbModalRef;
  private automaticRefreshFromExpiryMillis: number;
  private sessionExpiryFromNowMillis: number;
  private automaticRefreshEligibleMillis: number;

  constructor() {
    const router = this.router;

    this.decodedAuthToken$ = this.token$.pipe(
      map(tokenPair => tokenPair?.full ? parseUser(jwtHelperService.decodeToken(tokenPair.full.token)) : null),
      shareReplay(1)
    );
    this.decodedAccessToken$ = this.token$.pipe(
      map(tokenPair => tokenPair?.refresh ? parseUser(jwtHelperService.decodeToken(tokenPair.refresh.token)) : null),
      shareReplay(1)
    );

    this.token$.pipe(distinctUntilChanged()).subscribe(tokenPair => sessionStorage.setItem('token', JSON.stringify(tokenPair)));
    this.decodedAccessToken$.subscribe(decodedRefreshToken => this.onUpdatedRefreshToken(decodedRefreshToken));

    //Try to return the decoded refresh token, unless it's null.  In which case, return the first token.
    this.decodedToken$ = this.decodedAccessToken$.pipe(switchMap(v => v ? of(v) : this.decodedAuthToken$));

    //---------------------------------------------------------------------- There has to be a token that is not expired
    this.isUserLoggedIn$ = this.decodedAccessToken$.pipe(map(token => !!token && this.sessionExpiryFromNowMillis > 0), shareReplay(1));
    this.isFirstTimeUser$ = this.decodedToken$.pipe(map(jwt => containsIgnoreCase(jwt?.user?.status, 'NEW USER')), shareReplay(1));
    this.isPasswordExpired$ = this.decodedToken$.pipe(map(jwt => containsIgnoreCase(jwt?.user?.status, 'PASSWORD EXPIRED')), shareReplay(1));
    this.isUserPartiallyLoggedIn$ = this.decodedAuthToken$.pipe(map(token => !!token), shareReplay(1));

    this.userPermissions$ = this.buildUserPermissionsObs();
    this.homePage$ = this.buildHomePageObs();

    //Note: userPermissions$ must be defined before these observables
    this.isTuAdmin$ = this.userPermissions$.pipe(map(userTypeMap => userTypeMap?.tuAdmin ?? false), shareReplay(1));
    this.isAdmin$ = this.userPermissions$.pipe(map(userTypeMap => userTypeMap?.admin ?? false), shareReplay(1));
    this.isOperator$ = this.userPermissions$.pipe(map(userTypeMap => userTypeMap?.operator ?? false), shareReplay(1));
    this.isBusiness$ = this.userPermissions$.pipe(map(userTypeMap => userTypeMap?.business ?? false), shareReplay(1));
    this.isConsumer$ = this.userPermissions$.pipe(map(userTypeMap => userTypeMap?.consumer ?? false), shareReplay(1));

    this.isTuAdminOrAdmin$ = zip(this.isTuAdmin$, this.isAdmin$).pipe(map(([isTuAdmin, isAdmin]) => isTuAdmin || isAdmin), shareReplay(1));

    this.userName$ = this.decodedToken$.pipe(exists(), map(jwt => jwt?.user?.username ?? jwt?.sub), shareReplay(1));
    this.companyName$ = this.decodedToken$.pipe(exists(), map(jwt => jwt?.user?.company), shareReplay(1));
    this.userBranches$ = this.decodedAuthToken$.pipe(map(jwt => jwt?.user?.branches), shareReplay(1));

    this.myAdminNames$ = this.buildAdminNamesObs();

    // Build and subscribe to the observable that will handle refreshing the session.
    this.buildInitSessionRefreshObs().subscribe();

    // Monitor the router for any navigation.  We'll use it as a trigger to see if the token needs to be refreshed
    // The JWT Interceptor does the same thing for web requests
    router.events.pipe(
      filter(() => !!this.token$?.value),
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(() => {
      console.debug('Router refreshing session');
      this.refreshSession();
    });
  }

  private clearSubscriptions() {
    this._refreshSession?.unsubscribe();
    this._expiredSession?.unsubscribe();
    this._modalSubscription?.unsubscribe();
  }

  private resetSessionTimers() {
    // unsubscribe from session timers
    this.clearSubscriptions();

    // clear session timestamp
    this.automaticRefreshFromExpiryMillis = null;
  }

  /**
   * Logs out a user with an expired token, or configures the "you're about to be logged out" popup.
   */
  private onUpdatedRefreshToken(decodedRefreshToken: DecodedJwtToken) {
    // sessionStorage.setItem('decodedRefreshToken', JSON.stringify(decodedRefreshToken));
    if(decodedRefreshToken) {
      this.sessionExpiryFromNowMillis = expiryMillisFromNow(decodedRefreshToken);
      if(this.sessionExpiryFromNowMillis < 0) {
        //Should only happen when the user comes back to the site after a while and the token comes from sessionStorage
        console.info('Refresh token was expired. Logging out...');
        this.logout();
      } else {
        // console.debug(`Token iat ${reformatDateTime(decodedRefreshToken.iat * 1000)}, ${currentTimeSeconds() - decodedRefreshToken.iat} seconds ago`);
        // console.debug(`Token exp ${reformatDateTime(decodedRefreshToken.exp * 1000)}, ${expMillisFromNow / 1000} seconds from now`);
        // this.automaticRefreshFromExpiryMillis = maxTokenAgeMillis(decodedRefreshToken) / 2; //If token is half expired, it's eligible for automatic refresh.

        // After a 5s grace period, allow the token to be refreshed again (prevent pages that call multiple endpoints from ending up with double the calls
        // since each one would trigger a refresh.
        this.automaticRefreshFromExpiryMillis = 5000;
        this.automaticRefreshEligibleMillis = decodedRefreshToken.iat * 1000 + this.automaticRefreshFromExpiryMillis;

        // setTimeout(() => console.debug('Token is now eligible for refresh'), this.automaticRefreshFromExpiryMillis);
        // console.debug(
        //   `Token eligible for automatic refresh ${this.automaticRefreshFromExpiryMillis / 1000} seconds from iat at ` +
        //   `${reformatDateTime(this.automaticRefreshEligibleMillis)}`
        // );

        const notifyMillisFromNow = (this.sessionExpiryFromNowMillis - 60000); //Expiry minus one minute
        // const notifyTimeMillis = Date.now() + notifyMillisFromNow;
        // console.info(`Session notification reminder should happen at ${reformatDateTime(notifyTimeMillis)}`);

        this.clearSubscriptions();
        this._refreshSession = timer(notifyMillisFromNow).subscribe(() => this.onRefreshSessionTimer());
        this._expiredSession = timer(this.sessionExpiryFromNowMillis).subscribe(() => this.logout());
      }
    }
  }

  private onRefreshSessionTimer() {
    this._modalSubscription = this.isUserLoggedIn$.pipe(
      filter(b => b),
      tap(() => console.info('Session about to expire.  Asking user.')),
      map(() => this.modalService.open(
        {i18nKey: 'content.session-about-to-expire'},
        {i18nKey: 'content.session-about-to-expire-stay-logged-in'},
        'session-timeout-modal-label'
      )),
      tap(modal => this.refreshSessionModalRef = modal.ref),
      switchMap(modal => modal.result$)
    ).subscribe(accepted => {
      if(accepted) {
        console.debug(`OK clicked.  Refreshing Token...`);
        this.refreshSession(true);
      }
    });
  }

  /**
   * If the token is half-expired or more
   */
  private eligibleForAutomaticRefresh(): boolean {
    // console.debug('Checking if eligible for automatic refresh');
    if(this.token$?.value?.full) {
      // console.debug('this.token$?.value?.full is truthy');
      const currentMillis = Date.now();
      // console.debug(`Checking  if current time (${currentMillis}) is past when the token is eligible for refresh (${this.automaticRefreshEligibleMillis})`);
      return currentMillis >= this.automaticRefreshEligibleMillis;
    } else {
      return false;
    }
  }

  /**
   * Collect the permission-related data from the different places into one standard object.
   */
  private buildUserPermissionsObs(): Observable<UserPermissions> {
    return this.decodedAuthToken$.pipe(
      switchMap((decodedToken: DecodedJwtToken) => {
        if(decodedToken == null) {
          //No logged-in user
          return of(null);
        } else {
          return of(toUserPermissions(decodedToken.user.productIndicator.toLowerCase(), decodedToken.roles.split(',').map(s => s.trim())));
        }
      }),
      shareReplay(1)
    );
  }

  /** This observable watches the _manuallyInitSessionRefresh$ Subject */
  private buildInitSessionRefreshObs(): Observable<any> {
    return this._manuallyInitSessionRefresh$.pipe(
      // If many requests come through to refresh the session at the same time, we only need to action on one of them.
      // Take the first request, and then ignore any extras for a second
      throttleTime(1000),
      // Convert the primary Subject for this stream into a set of flags.  The primary Subject itself emits a boolean which we use as a force flag
      switchMap((forceRefresh: boolean) => zip(
        // Create a validForRefresh flag which considers if the token exists and is either eligible to be refreshed or the force flag was set to true
        of(this.token$.value && (forceRefresh || this.eligibleForAutomaticRefresh())),
        this.isFirstTimeUser$, this.isPasswordExpired$ // Self-explanatory flags, maintained by other streams
      )),
      switchMap(([validForRefresh, isFirstTimeUser, isPasswordExpired]: [boolean, boolean, boolean]) => {
        // console.debug('validForRefresh', validForRefresh);
        // console.debug('isFirstTimeUser', isFirstTimeUser);
        // console.debug('isPasswordExpired', isPasswordExpired);
        if(validForRefresh && !isFirstTimeUser && !isPasswordExpired) {
          // If everything has passed up to this point in the stream, we'll perform the actual refresh.
          console.debug('Automatically refreshing token');
          this.clearSubscriptions();
          return this.securityService.refreshToken(this.token$.value.full).pipe(first(), tap(tokenPair => {
            if(isTokenPair(tokenPair)) {
              this.token$.next(tokenPair);
            }
          }));
        } else {
          return of(null);
        }
      })
    );
  }

  /**
   * Builds the homePage$ observable.
   *
   * @private
   */
  private buildHomePageObs(): Observable<string> {

    //If it's a user that isn't fully logged-in (first-time user, "you must add an MFA device"), make the homePage the login screen.
    //To do that, we'll send them to logout which will clear everything and forward them to login
    //This is because intuitively someone trying to escape the first-time-login process will click the TU Direct link in the header which is the home page.
    return this.isUserLoggedIn$.pipe(
      switchMap(isUserLoggedIn => {
        if(isUserLoggedIn) {
          //If they are a normal user, we will send them to a page base on their permissions
          return this.userPermissions$.pipe(
            switchMap((permissions: UserPermissions) => {
              let homePage$: Observable<string> = of('/');
              if(!!permissions) {
                if(permissions.consumer) {
                  homePage$ = of('/consumer-services');
                } else if(permissions.business) {
                  homePage$ = of('/business-services');
                } else if(permissions.tuAdmin || permissions.operator) {
                  homePage$ = of('/admin');
                } else if(permissions.admin) {
                  // A normal company admin's home page is for their specific company.
                  homePage$ = this.companyName$.pipe(map(companyName => `/admin/companies/${encodeURIComponent(companyName)}/details`));
                }
              }
              return homePage$;
            })
          );
        } else {
          //If the user isn't logged in, check if they are at least partially logged-in (first-time user, mfa).
          //A partial log-in will intuitively want to be cancelled when clicking the header "home" page
          return this.isUserPartiallyLoggedIn$.pipe(map(isUserPartiallyLoggedIn => isUserPartiallyLoggedIn ? '/logout' : '/login'));
        }
      }),
      // tap(url => console.debug('Final homepage url', url)),
      shareReplay(1)
    );
  }

  private buildAdminNamesObs(): Observable<string[]> {
    return this.decodedAccessToken$.pipe(
      // convert to company name
      map(token => token?.user?.company),
      // Prevent unnecessary calls by only proceeding if the company name has changed since last time
      distinctUntilChanged(),
      // we only want valid company names
      filter(isNotBlank),
      // use the company name to pull the admins
      switchMap(company => this.companiesService.getCompanyAdmins(company)),
      // convert to just their names
      map(admins => admins.map(admin => this.usersService.getUserDisplayName(admin))),
      shareReplay(1) //Cache the response for future subscribers
    );
  }

  /**
   * Do step 1 of an MFA login.
   */
  startLogin(userName: string, password: string): Observable<RawDecodedTokenPair<UserDetails> | number> {
    this.resetSessionTimers();
    return this.securityService.logIn(userName, password).pipe(
      // Publish the token
      tap(([status, token]) => this.token$.next(isError(status) ? null : {full: token} as TokenPair<any, any>)),
      map(([status, token]: [number, JwtToken<UserDetails>]) => {
        //check the status if number it will return number
        if(_.isFinite(status)) {
          if(status === 200) {
            return {token: token, decodedToken: parseUser(jwtHelperService.decodeToken(token.token))} as RawDecodedTokenPair<UserDetails>;
          } else {
            return status;
          }
        } else if(isError(status)) {
          return null;
        } else {
          // Decode the token and return the encoded/decoded token back
          return {token: token, decodedToken: parseUser(jwtHelperService.decodeToken(token.token))} as RawDecodedTokenPair<UserDetails>;
        }
      })
    );
  }

  finishLogin(userName: string, jwtToken: JwtToken<any>): Observable<boolean | number> {
    return this.securityService.refreshToken(jwtToken).pipe(map((tokenPairOrStatus: TokenPair<any, any> | number) => {
      if(isTokenPair(tokenPairOrStatus)) {
        this.token$.next(tokenPairOrStatus);
        return true;
      } else if(_.isFinite(tokenPairOrStatus)) {
        console.debug('Statuscode of Token Refreshed ');
        return tokenPairOrStatus;
      } else {
        return false;
      }
    }));
  }

  logout() {
    // remove user from session storage
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('decodedJwtToken');
    sessionStorage.removeItem('userDetails');

    // remove various cached items from session storage
    [BUSINESS_REQUEST_CACHE_KEY, BUSINESS_RESPONSE_CACHE_KEY, CONSUMER_REQUEST_CACHE_KEY, CONSUMER_RESPONSE_CACHE_KEY, IFS_REQUEST_CACHE_KEY, IFS_RESPONSE_CACHE_KEY]
      .forEach(s => sessionStorage.removeItem(s));

    // clear vars
    this.token$.next(null);
    // this.decodedAuthToken$.next(null);
    // this.decodedAccessToken$.next(null);

    this.resetSessionTimers();

    // Remove refresh modal if it's present
    this.refreshSessionModalRef?.close();

    // send user back to login page
    if(!this.router.isActive('/login', false)) {
      this.router.navigateByUrl('/login').then(() => console.info('Logout complete'));
    }

    //Notify any observers of the logout$ Observable that a logout has happened
    this.logout$.emit();

  }

  refreshSession(force = false): void {
    this._manuallyInitSessionRefresh$.next(force);
  }

  /**
   * Convenience method to help answer questions like "User can do this if they are a TU Admin OR ..."
   *
   * Will essentially return true if the user is a TU Admin or ANY of the passed-in observables return true.
   *
   * @param notTuAdmin$ - A list of Observable<boolean> to check if the user is not a TU Admin
   */
  isTuAdminOrAny(...notTuAdmin$: Observable<boolean>[]) {
    return this.isTuAdmin$.pipe(switchMap(isTuAdmin =>
      isTuAdmin ? of(isTuAdmin) : anyTrue(...notTuAdmin$)
    ));
  }

  /**
   * Find out if the given user object is the currently logged-in user.
   *
   * @param user - Can be a {@link User} object or just the username as a string
   */
  isMe(user: User | string): Observable<boolean> {
    return this.userName$.pipe(
      map(userName => userName === ((typeof user === 'string') ? user : user.userName)),
      first()
    );
  }

  /**
   * Inverts the response from {@link isMe}
   */
  isNotMe(user: User | string): Observable<boolean> {
    return this.isMe(user).pipe(map(v => !v), first());
  }

  get currentJwtTokenPair(): TokenPair<any, any> {
    return this.token$.value;
  }

  // private get sessionExpiryFromNowMillis() {
  //   return expiryMillisFromNow(this.decodedAccessToken$.value);
  // }

}
